// One conversation's messages, oldest first, with a box to reply (#334) and a Report button (#336).
//
// Text shows as plain text: React escapes it, white-space keeps the writer's line breaks, and a
// link is never made clickable (a message can carry a phishing link). There is no live connection:
// the thread asks for new messages every THREAD_REFRESH_MS while it is open and stops when it closes.
//
// mode: 'member' (one of the two people) or 'admin' (reading a reported conversation). Admin mode
// has no reply box and no Report button, and loads once: every admin load writes an activity log
// row, so it never refreshes on a timer.
// onRead: called after each load, which marks the reader's unread messages as read.
import React from 'react';
import { api } from '../../lib/api';
import { Btn } from '../index';
import { palette as T, type } from '../../design';

export const THREAD_REFRESH_MS = 30_000;
// Mirrors MESSAGE_MAX_LENGTH in backend/src/lib/constants.js; the API refuses anything longer.
export const MESSAGE_MAX = 2000;
// Mirrors CONVERSATION_REPORT_REASON_MAX_LENGTH in backend/src/lib/constants.js.
export const REPORT_REASON_MAX = 500;
export const REPORT_SENT = 'You reported this conversation. Our team will read it and email you if we need more from you.';
export const ADMIN_LOAD_FAILED = 'We could not load this conversation. Check your connection and tap Try again.';
export const ADMIN_NOT_OPEN ='This conversation has no open report, so you cannot read it. Refresh the Reported conversations tab.';
const MEMBER_MODE = 'member';
const STUDENT_ROLE = 'student';
const NOT_FOUND = 404;

const FONT = "'Plus Jakarta Sans', sans-serif";

const LOAD_FAILED = 'We could not load this conversation. Check your connection; we try again every 30 seconds.';
const SEND_FAILED = 'We could not send your message. Check your connection and send it again.';
const REPORT_FAILED = 'We could not send your report. Check your connection and send it again.';

function formatTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// The server's list, plus any message this screen just sent that a refresh started earlier missed.
function mergeMessages(server, local) {
  const known = new Set(server.map((m) => m.id));
  const last = server.length ? server[server.length - 1].createdAt : '';
  const missed = local.filter((m) => m.mine && !known.has(m.id) && m.createdAt >= last);
  return [...server, ...missed];
}

function senderName(message, conversation, mode) {
  if (mode === MEMBER_MODE) return message.mine ? 'You' : (conversation?.with?.name || 'Them');
  const side = message.senderRole === STUDENT_ROLE ? conversation?.student : conversation?.instructor;
  return side?.name || message.senderRole;
}

function MessageBubble({ message, conversation, mode }) {
  const mine = mode === MEMBER_MODE && message.mine;
  return (
    <li style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', margin: '0 0 12px' }}>
      <span style={{ fontFamily: FONT, fontSize: type.caption, color: T.driftwood, margin: '0 4px 4px' }}>
        {senderName(message, conversation, mode)} · {formatTime(message.createdAt)}
      </span>
      <p
        data-testid="message-text"
        style={{
          fontFamily: FONT, fontSize: type.body, lineHeight: 1.5, color: T.espresso, margin: 0,
          padding: '10px 14px', borderRadius: 16, maxWidth: '85%',
          background: mine ? T.messageMine : T.white, border: '1px solid ' + T.border,
          whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
        }}
      >
        {message.text}
      </p>
    </li>
  );
}

function Notice({ children }) {
  return (
    <p role="alert" style={{ fontFamily: FONT, fontSize: type.body, color: T.errorRed, margin: '0 0 12px' }}>{children}</p>
  );
}

// The Report button and its form. The report goes to admin only; the other person is not told.
function ReportConversation({ conversationId }) {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState('');
  const [sent, setSent] = React.useState(false);

  React.useEffect(() => {
    setOpen(false);
    setReason('');
    setError('');
    setSent(false);
  }, [conversationId]);

  const submit = async () => {
    if (sending) return;
    setSending(true);
    setError('');
    try {
      const trimmed = reason.trim();
      await api.post(`/conversations/${conversationId}/report`, trimmed ? { reason: trimmed } : {});
      setSent(true);
      setOpen(false);
      setReason('');
    } catch (err) {
      console.error('[messages] could not report the conversation', err.status ?? err);
      setError(err.data?.message || REPORT_FAILED);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return <p role="status" style={{ fontSize: type.body, color: T.walnut, margin: 0 }}>{REPORT_SENT}</p>;
  }

  if (!open) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: type.body, fontWeight: 600, color: T.errorRed, padding: 0 }}
        >
          Report conversation
        </button>
      </div>
    );
  }

  return (
    <div style={{ background: T.white, border: '1px solid ' + T.border, borderRadius: 16, padding: 14 }}>
      <label htmlFor={`report-${conversationId}`} style={{ display: 'block', fontSize: type.body, fontWeight: 700, color: T.espresso, margin: '0 0 4px' }}>
        Why are you reporting this conversation? (optional)
      </label>
      <p style={{ fontSize: type.label, color: T.driftwood, margin: '0 0 8px' }}>
        Our team can read this conversation while your report is open. The other person is not told.
      </p>
      <textarea
        id={`report-${conversationId}`}
        value={reason}
        maxLength={REPORT_REASON_MAX}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid ' + T.border,
          fontFamily: FONT, fontSize: type.body, color: T.espresso, resize: 'vertical', outline: 'none',
          boxSizing: 'border-box', background: T.white,
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', margin: '6px 0 0' }}>
        <span style={{ fontSize: type.caption, color: T.driftwood }}>{reason.length} / {REPORT_REASON_MAX}</span>
        <span style={{ display: 'flex', gap: 8 }}>
          <Btn size="sm" variant="subtle" onClick={() => { setOpen(false); setError(''); }} disabled={sending}>Cancel</Btn>
          <Btn size="sm" onClick={submit} disabled={sending}>{sending ? 'Sending…' : 'Send report'}</Btn>
        </span>
      </div>
      {error && <div style={{ marginTop: 10 }}><Notice>{error}</Notice></div>}
    </div>
  );
}

export function ConversationThread({ conversationId, mode = MEMBER_MODE, onRead }) {
  const [conversation, setConversation] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');
  const [notFound, setNotFound] = React.useState(false);
  const [text, setText] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState('');
  // Admin mode never refreshes on its own; Try again bumps this to load once more.
  const [attempt, setAttempt] = React.useState(0);
  const listRef = React.useRef(null);
  const onReadRef = React.useRef(onRead);
  onReadRef.current = onRead;

  React.useEffect(() => {
    let open = true;
    let busy = false;
    let timer = null;
    setConversation(null);
    setMessages([]);
    setLoaded(false);
    setLoadError('');
    setNotFound(false);

    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    const load = async () => {
      if (busy) return;
      busy = true;
      try {
        const data = await api.get(`/conversations/${conversationId}/messages`);
        if (!open) return;
        setConversation(data.conversation);
        setMessages((local) => mergeMessages(data.messages || [], local));
        setLoadError('');
        setLoaded(true);
        onReadRef.current?.();
      } catch (err) {
        if (!open) return;
        if (err.status === NOT_FOUND) {
          // Not theirs, or deleted: asking again every 30 seconds cannot change that.
          setNotFound(true);
          stop();
        }
        console.error('[messages] could not load the conversation', err.status ?? err);
        if (mode === MEMBER_MODE) setLoadError(err.data?.message || LOAD_FAILED);
        else setLoadError(err.status === NOT_FOUND ? ADMIN_NOT_OPEN : ADMIN_LOAD_FAILED);
      } finally {
        busy = false;
      }
    };

    // Admin: one load, started a tick later, so a mount that unmounts at once (React StrictMode in
    // development) never sends it and one open writes one activity log row.
    let adminStart = null;
    if (mode === MEMBER_MODE) {
      load();
      timer = setInterval(load, THREAD_REFRESH_MS);
    } else {
      adminStart = setTimeout(load, 0);
    }
    return () => {
      open = false;
      if (adminStart) clearTimeout(adminStart);
      stop();
    };
  }, [conversationId, mode, attempt]);

  // Keep the newest message in view.
  React.useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages.length]);

  const trimmed = text.trim();
  const canSend = trimmed.length > 0 && trimmed.length <= MESSAGE_MAX && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setSendError('');
    try {
      const data = await api.post(`/conversations/${conversationId}/messages`, { text: trimmed });
      setMessages((list) => (list.some((m) => m.id === data.message.id) ? list : [...list, data.message]));
      setText('');
    } catch (err) {
      setSendError(err.data?.message || SEND_FAILED);
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <div style={{ padding: 16 }}>
        <Notice>{loadError}</Notice>
      </div>
    );
  }

  const title = mode === MEMBER_MODE
    ? `Conversation with ${conversation?.with?.name || '…'}`
    : `Conversation between ${conversation?.student?.name || 'the student'} and ${conversation?.instructor?.name || 'the instructor'}`;

  return (
    <section aria-label="Conversation" style={{ display: 'flex', flexDirection: 'column', gap: 12, fontFamily: FONT }}>
      <h3 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h4, color: T.espresso, margin: 0 }}>{title}</h3>

      {loadError && <Notice>{loadError}</Notice>}
      {loadError && mode !== MEMBER_MODE && (
        <div><Btn size="sm" variant="subtle" onClick={() => setAttempt((n) => n + 1)}>Try again</Btn></div>
      )}

      <ul
        ref={listRef}
        style={{
          listStyle: 'none', margin: 0, padding: 12, maxHeight: 480, overflowY: 'auto',
          background: T.ivory, border: '1px solid ' + T.border, borderRadius: 16,
        }}
      >
        {!loaded && !loadError && (
          <li style={{ fontSize: type.body, color: T.driftwood }}>Loading messages…</li>
        )}
        {loaded && messages.length === 0 && (
          <li style={{ fontSize: type.body, color: T.driftwood }}>
            {mode === MEMBER_MODE ? 'No messages yet. Write the first one below.' : 'This conversation has no messages.'}
          </li>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} conversation={conversation} mode={mode} />
        ))}
      </ul>

      {mode === MEMBER_MODE && (
        <div>
          <label htmlFor={`reply-${conversationId}`} style={{ display: 'block', fontSize: type.body, fontWeight: 700, color: T.espresso, margin: '0 0 8px' }}>
            Your message
          </label>
          <textarea
            id={`reply-${conversationId}`}
            value={text}
            maxLength={MESSAGE_MAX}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="Write a message"
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid ' + T.border,
              fontFamily: FONT, fontSize: type.body, color: T.espresso, resize: 'vertical', outline: 'none',
              boxSizing: 'border-box', background: T.white,
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, margin: '6px 0 0' }}>
            <span style={{ fontSize: type.caption, color: T.driftwood }}>{text.length} / {MESSAGE_MAX}</span>
            <Btn onClick={send} disabled={!canSend}>{sending ? 'Sending…' : 'Send'}</Btn>
          </div>
          {sendError && <div style={{ marginTop: 10 }}><Notice>{sendError}</Notice></div>}
        </div>
      )}

      {mode === MEMBER_MODE && loaded && <ReportConversation conversationId={conversationId} />}
    </section>
  );
}
